import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { bankData } from '../data/bankData'
import { translations } from '../data/translations'

const AppContext = createContext()

export const useApp = () => {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useApp must be used within AppProvider')
  }
  return context
}

export const AppProvider = ({ children }) => {
  // 🌐 Language State
  const [currentLang, setCurrentLang] = useState('zh')
  
  // 🌙 Theme State
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') === 'dark' || 
             (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches)
    }
    return false
  })

  // 🏦 Bank Data State - 進一步簡化和優化
  const [allBanks] = useState([...bankData])
  const [searchQuery, setSearchQuery] = useState('')
  const [activeRange, setActiveRange] = useState('all')
  const [sortField, setSortField] = useState('code')
  const [sortDirection, setSortDirection] = useState('asc')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [isSearching, setIsSearching] = useState(false)

  // 🚀 Performance refs - 統一和優化的 debounce/throttle 管理
  const searchCacheRef = useRef(new Map())
  const debounceTimerRef = useRef(null)
  const throttleTimerRef = useRef(null)
  const lastSearchTimeRef = useRef(0)

  // 🌐 Language Functions
  const toggleLanguage = useCallback(() => {
    setCurrentLang(prev => prev === 'zh' ? 'en' : 'zh')
  }, [])

  // 🌙 Theme Functions
  const toggleTheme = useCallback(() => {
    setIsDarkMode(prev => {
      const newTheme = !prev
      localStorage.setItem('theme', newTheme ? 'dark' : 'light')
      document.documentElement.classList.toggle('dark', newTheme)
      return newTheme
    })
  }, [])

  // 🌙 Theme Effect
  useEffect(() => {
    document.documentElement.classList.toggle('dark', isDarkMode)
  }, [isDarkMode])

  // 🌐 Enhanced SEO Effect
  useEffect(() => {
    document.title = translations.pageTitle[currentLang]
    document.documentElement.lang = currentLang === 'zh' ? 'zh-HK' : 'en'
    
    const updateMeta = (selector, content) => {
      const meta = document.querySelector(selector)
      if (meta) meta.setAttribute('content', content)
    }
    
    updateMeta('meta[name="description"]', translations.pageDescription[currentLang])
    updateMeta('meta[property="og:title"]', translations.pageTitle[currentLang])
    updateMeta('meta[property="og:description"]', translations.pageDescription[currentLang])
    updateMeta('meta[name="twitter:title"]', translations.pageTitle[currentLang])
    updateMeta('meta[name="twitter:description"]', translations.pageDescription[currentLang])

    // Update JSON-LD structured data
    const jsonLdScript = document.querySelector('script[type="application/ld+json"]')
    if (jsonLdScript) {
      try {
        const structuredData = JSON.parse(jsonLdScript.textContent)
        structuredData.name = translations.pageTitle[currentLang]
        structuredData.description = translations.pageDescription[currentLang]
        jsonLdScript.textContent = JSON.stringify(structuredData, null, 2)
      } catch (e) {
        console.warn('Failed to update structured data:', e)
      }
    }
  }, [currentLang])

  // 🔍 統一且優化的過濾邏輯 - 進一步合併和優化
  const filteredBanks = useMemo(() => {
    const startTime = performance.now()
    let banks = [...allBanks]
    
    // 範圍篩選（優先級較低，當有搜尋時會被忽略）
    if (activeRange !== 'all' && !searchQuery.trim()) {
      const ranges = {
        '001-030': [1, 30],
        '031-060': [31, 60],
        '061-090': [61, 90],
        '091-120': [91, 120],
        '121-150': [121, 150],
        '151-180': [151, 180],
        '181-210': [181, 210],
        '211-240': [211, 240],
        '241-270': [241, 270],
        '271-300': [271, 300],
        '301+': [301, 999]
      }
      
      const [start, end] = ranges[activeRange] || [0, 999]
      banks = banks.filter(bank => {
        const code = parseInt(bank.code)
        return code >= start && code <= end
      })
    }
    
    // 搜尋篩選（優先級較高）
    if (searchQuery.trim()) {
      const searchTerm = searchQuery.toLowerCase().trim()
      
      // 智能快取管理
      if (searchCacheRef.current.has(searchTerm)) {
        const cachedResult = searchCacheRef.current.get(searchTerm)
        // 更新快取使用時間
        cachedResult.lastUsed = Date.now()
        return cachedResult.data
      }
      
      // 執行搜尋
      banks = banks.filter(bank => {
        return (
          bank.code.toLowerCase().includes(searchTerm) ||
          bank.nameEn.toLowerCase().includes(searchTerm) ||
          (bank.nameZh !== '-' && bank.nameZh.toLowerCase().includes(searchTerm))
        )
      })
      
      // 智能快取管理 - 清理舊的快取項目
      if (searchCacheRef.current.size > 100) {
        const entries = Array.from(searchCacheRef.current.entries())
        entries.sort((a, b) => (a[1].lastUsed || 0) - (b[1].lastUsed || 0))
        
        // 刪除最舊的50%項目
        const toDelete = entries.slice(0, Math.floor(entries.length / 2))
        toDelete.forEach(([key]) => searchCacheRef.current.delete(key))
      }
      
      // 儲存到快取
      searchCacheRef.current.set(searchTerm, {
        data: banks,
        lastUsed: Date.now(),
        createdAt: Date.now()
      })
    }
    
    // 性能監控
    const endTime = performance.now()
    if (endTime - startTime > 50) {
      console.warn(`Filtering took ${endTime - startTime}ms for ${banks.length} results`)
    }
    
    return banks
  }, [allBanks, searchQuery, activeRange])

  // 🔄 Enhanced Sort Function - 優化排序邏輯
  const sortedBanks = useMemo(() => {
    return [...filteredBanks].sort((a, b) => {
      let valueA, valueB

      if (sortField === 'code') {
        valueA = parseInt(a.code)
        valueB = parseInt(b.code)
      } else if (sortField === 'nameEn') {
        valueA = a.nameEn.toLowerCase()
        valueB = b.nameEn.toLowerCase()
      } else if (sortField === 'nameZh') {
        valueA = a.nameZh !== '-' ? a.nameZh.toLowerCase() : 'zzz'
        valueB = b.nameZh !== '-' ? b.nameZh.toLowerCase() : 'zzz'
      }

      const comparison = valueA > valueB ? 1 : valueA < valueB ? -1 : 0
      return sortDirection === 'asc' ? comparison : -comparison
    })
  }, [filteredBanks, sortField, sortDirection])

  // 📄 Enhanced Pagination - 智能分頁邏輯
  const totalPages = Math.max(1, Math.ceil(sortedBanks.length / itemsPerPage))
  
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const paginatedBanks = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return sortedBanks.slice(startIndex, endIndex)
  }, [sortedBanks, currentPage, itemsPerPage])

  // 🔍 Enhanced Search Effect with Smart Debouncing
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    if (!searchQuery.trim()) {
      setIsSearching(false)
      setCurrentPage(1)
      return
    }
    
    // 智能搜尋狀態管理
    const currentTime = Date.now()
    const timeSinceLastSearch = currentTime - lastSearchTimeRef.current
    
    // 如果搜尋間隔很短，使用較短的延遲
    const debounceDelay = timeSinceLastSearch < 500 ? 150 : 250
    
    setIsSearching(true)
    
    debounceTimerRef.current = setTimeout(() => {
      setIsSearching(false)
      setCurrentPage(1)
      setActiveRange('all') // 搜尋時自動清除範圍篩選
      lastSearchTimeRef.current = Date.now()
    }, debounceDelay)

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [searchQuery])

  // 🎯 Enhanced Quick Navigation
  const handleQuickNavRange = useCallback((range) => {
    if (searchQuery.trim()) {
      setSearchQuery('')
    }
    
    setCurrentPage(1)
    setActiveRange(range.id)
    setSortField('code')
    setSortDirection('asc')
  }, [searchQuery])

  // 🎯 Enhanced Handlers
  const handleSort = useCallback((field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc'
    setSortField(field)
    setSortDirection(newDirection)
    setCurrentPage(1)
  }, [sortField, sortDirection])

  // Enhanced pagination functions with smart scrolling
  const goToPage = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== currentPage) {
      setCurrentPage(newPage)
      
      // 智能滾動管理
      if (!throttleTimerRef.current) {
        throttleTimerRef.current = setTimeout(() => {
          const resultsSection = document.getElementById('results-section')
          if (resultsSection) {
            const rect = resultsSection.getBoundingClientRect()
            const isVisible = rect.top >= -100 && rect.top <= window.innerHeight * 0.3
            
            if (!isVisible) {
              resultsSection.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              })
            }
          }
          throttleTimerRef.current = null
        }, 100)
      }
    }
  }, [totalPages, currentPage])

  const goToPreviousPage = useCallback(() => {
    goToPage(currentPage - 1)
  }, [currentPage, goToPage])

  const goToNextPage = useCallback(() => {
    goToPage(currentPage + 1)
  }, [currentPage, goToPage])

  const canGoPrevious = currentPage > 1
  const canGoNext = currentPage < totalPages

  // 🧹 Enhanced Cleanup functions
  const clearSearch = useCallback(() => {
    setSearchQuery('')
    setCurrentPage(1)
    setIsSearching(false)
  }, [])

  const clearActiveRange = useCallback(() => {
    setActiveRange('all')
    setCurrentPage(1)
    setSortField('code')
    setSortDirection('asc')
  }, [])

  // 📊 Enhanced Statistics
  const startIndex = sortedBanks.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0
  const endIndex = Math.min(currentPage * itemsPerPage, sortedBanks.length)
  const totalCount = sortedBanks.length
  const originalTotalCount = allBanks.length

  // 🧹 Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current)
      }
    }
  }, [])

  // 🎯 Enhanced Context Value
  const value = useMemo(() => ({
    // Language
    currentLang,
    toggleLanguage,
    isChineseActive: currentLang === 'zh',
    
    // Theme
    isDarkMode,
    toggleTheme,
    
    // Data
    allBanks,
    filteredBanks: sortedBanks,
    paginatedBanks,
    
    // Search
    searchQuery,
    setSearchQuery,
    isSearching,
    clearSearch,
    
    // Filter
    activeRange,
    setActiveRange,
    setQuickNavRange: handleQuickNavRange,
    clearActiveRange,
    
    // Sort
    sortField,
    sortDirection,
    handleSort,
    
    // Pagination
    currentPage,
    totalPages,
    itemsPerPage,
    setItemsPerPage,
    goToPage,
    goToPreviousPage,
    goToNextPage,
    canGoPrevious,
    canGoNext,
    
    // Statistics
    startIndex,
    endIndex,
    totalCount,
    originalTotalCount,
    
    // Translations
    translations
  }), [
    currentLang, toggleLanguage, isDarkMode, toggleTheme,
    allBanks, sortedBanks, paginatedBanks,
    searchQuery, isSearching, clearSearch,
    activeRange, handleQuickNavRange, clearActiveRange,
    sortField, sortDirection, handleSort,
    currentPage, totalPages, itemsPerPage, goToPage, goToPreviousPage, goToNextPage, canGoPrevious, canGoNext,
    startIndex, endIndex, totalCount, originalTotalCount,
    translations
  ])

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  )
}